import { useState, useEffect } from 'react';
import { Plus, MagnifyingGlass, ArrowRight, Buildings, CurrencyInr, X, MapPin, PencilSimple, Trash } from '@phosphor-icons/react';
import api from '../api';
import WorkflowActions from '../components/WorkflowActions';
import { useAuth } from '../contexts/AuthContext';

const mono = { fontFamily: "'JetBrains Mono', monospace" };

const BLANK_LOC = { nickname: '', address: '', city: '', state: '', pincode: '', gstin: '', contact_name: '', contact_phone: '', type: 'both' };

function LocFormFields({ form, onChange }) {
  const field = (key, label, span1) => (
    <div key={key} style={{ gridColumn: span1 ? 'span 1' : 'span 2' }}>
      <label style={{ display: 'block', ...mono, fontSize: 9, color: 'hsl(0,0%,45%)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 4 }}>{label}</label>
      <input value={form[key] || ''} onChange={e => onChange(key, e.target.value)}
        style={{ width: '100%', background: 'hsl(0,0%,8%)', border: '1px solid hsl(0,0%,18%)', padding: '7px 10px', color: 'hsl(0,0%,85%)', ...mono, fontSize: 11, outline: 'none', boxSizing: 'border-box' }} />
    </div>
  );
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
      {field('nickname', 'Nickname')}
      {field('gstin', 'GSTIN', true)}
      {field('address', 'Address')}
      {field('city', 'City', true)}
      {field('state', 'State', true)}
      {field('pincode', 'Pincode', true)}
      {field('contact_name', 'Contact Name', true)}
      {field('contact_phone', 'Contact Phone', true)}
      <div style={{ gridColumn: 'span 1' }}>
        <label style={{ display: 'block', ...mono, fontSize: 9, color: 'hsl(0,0%,45%)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 4 }}>Type</label>
        <select value={form.type || 'both'} onChange={e => onChange('type', e.target.value)}
          style={{ width: '100%', background: 'hsl(0,0%,8%)', border: '1px solid hsl(0,0%,18%)', padding: '7px 10px', color: 'hsl(0,0%,85%)', ...mono, fontSize: 11, outline: 'none', boxSizing: 'border-box' }}>
          <option value="both">Both (Billing + Shipping)</option>
          <option value="billing">Billing Only</option>
          <option value="shipping">Shipping Only</option>
        </select>
      </div>
    </div>
  );
}

function typeBadge(type) {
  if (type === 'billing')  return { color: 'hsl(142,70%,55%)', border: '1px solid hsl(142,50%,25%)', background: 'hsl(142,30%,8%)' };
  if (type === 'shipping') return { color: 'hsl(197,100%,48%)', border: '1px solid hsl(197,60%,25%)', background: 'hsl(197,30%,8%)' };
  return { color: 'hsl(270,60%,65%)', border: '1px solid hsl(270,40%,25%)', background: 'hsl(270,30%,8%)' };
}

function StatusChip({ status }) {
  return <span className={status === 'active' ? 'chip-active' : 'chip-neutral'}>{status?.toUpperCase()}</span>;
}

function CategoryChip({ cat }) {
  return <span className={cat === 'premium' ? 'chip-warning' : 'chip-neutral'}>{cat?.toUpperCase()}</span>;
}

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', code: '', gstin: '', city: '', state: '',
    contact_name: '', contact_email: '', contact_phone: '', whatsapp_phone: '',
    payment_terms: '30 days', credit_limit: '', category: 'standard', currency: 'INR' });

  const { user } = useAuth();

  // Location management state
  const [locForm, setLocForm] = useState(BLANK_LOC);
  const [locEditId, setLocEditId] = useState(null);   // null = add mode, 'hq' / hex = edit mode
  const [locSaving, setLocSaving] = useState(false);
  const [showLocForm, setShowLocForm] = useState(false);

  useEffect(() => { loadCustomers(); }, []);

  const loadCustomers = async () => {
    try {
      const r = await api.get('/customers');
      setCustomers(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadDetail = async (id) => {
    try {
      const r = await api.get(`/customers/${id}`);
      setDetail(r.data);
    } catch (e) { console.error(e); }
  };

  const handleSelect = (c) => {
    setSelected(c.id);
    loadDetail(c.id);
    setShowLocForm(false);
    setLocEditId(null);
    setLocForm(BLANK_LOC);
  };

  const openAddLoc = () => { setLocForm(BLANK_LOC); setLocEditId(null); setShowLocForm(true); };
  const openEditLoc = (loc) => { setLocForm({ ...loc }); setLocEditId(loc.loc_id); setShowLocForm(true); };
  const cancelLocForm = () => { setShowLocForm(false); setLocEditId(null); setLocForm(BLANK_LOC); };

  const saveLoc = async () => {
    if (!detail) return;
    setLocSaving(true);
    try {
      if (locEditId) {
        await api.put(`/customers/${detail.id}/locations/${locEditId}`, locForm);
      } else {
        await api.post(`/customers/${detail.id}/locations`, locForm);
      }
      await loadDetail(detail.id);
      cancelLocForm();
    } catch (e) { console.error(e); }
    setLocSaving(false);
  };

  const deleteLoc = async (locId) => {
    if (!detail) return;
    if (!window.confirm('Delete this location?')) return;
    try {
      await api.delete(`/customers/${detail.id}/locations/${locId}`);
      await loadDetail(detail.id);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Cannot delete location';
      alert(msg);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await api.post('/customers', { ...form, credit_limit: parseFloat(form.credit_limit) || 0 });
      setShowAdd(false);
      setForm({ name: '', code: '', gstin: '', city: '', state: '', contact_name: '',
        contact_email: '', contact_phone: '', whatsapp_phone: '',
        payment_terms: '30 days', credit_limit: '', category: 'standard', currency: 'INR' });
      loadCustomers();
    } catch (e) { console.error(e); }
  };

  const filtered = customers.filter(c =>
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.code?.toLowerCase().includes(search.toLowerCase()) ||
    c.city?.toLowerCase().includes(search.toLowerCase())
  );

  const fmt = (n) => n ? `₹${(n/1000).toFixed(0)}K` : '₹0';
  const isOverLimit = (c) => c.outstanding > c.credit_limit;

  return (
    <div data-testid="customers-page" style={{ display: 'flex', gap: 1, height: 'calc(100vh - 96px)' }} className="split-view">
      {/* Left list */}
      <div className="split-left" style={{ width: 380, display: 'flex', flexDirection: 'column',
        background: 'hsl(0,0%,4%)', border: '1px solid hsl(0,0%,14%)' }}>
        {/* Header */}
        <div style={{ padding: '16px', borderBottom: '1px solid hsl(0,0%,12%)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h2 style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 16, fontWeight: 700,
              color: 'hsl(0,0%,95%)', textTransform: 'uppercase' }}>Customers</h2>
            <button data-testid="add-customer-btn" onClick={() => setShowAdd(true)} className="btn-primary"
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', fontSize: 11 }}>
              <Plus size={12} weight="bold" /> ADD
            </button>
          </div>
          <div style={{ position: 'relative' }}>
            <MagnifyingGlass size={14} color="hsl(0,0%,40%)"
              style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
            <input data-testid="customer-search" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search customers..."
              style={{ width: '100%', background: 'hsl(0,0%,7%)', border: '1px solid hsl(0,0%,16%)',
                padding: '8px 10px 8px 30px', color: 'hsl(0,0%,80%)', fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11, outline: 'none', boxSizing: 'border-box' }}
              onFocus={e => e.target.style.borderColor = 'hsl(197,100%,48%)'}
              onBlur={e => e.target.style.borderColor = 'hsl(0,0%,16%)'} />
          </div>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: 20, color: 'hsl(0,0%,40%)', fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>
              Loading...
            </div>
          ) : filtered.map(c => (
            <div key={c.id} data-testid={`customer-item-${c.code}`}
              onClick={() => handleSelect(c)}
              style={{ padding: '12px 16px', borderBottom: '1px solid hsl(0,0%,10%)', cursor: 'pointer',
                background: selected === c.id ? 'hsl(0,0%,7%)' : 'transparent', transition: 'background 0.1s',
                borderLeft: isOverLimit(c) ? '3px solid hsl(0,84%,60%)' : '3px solid transparent' }}
              onMouseEnter={e => { if (selected !== c.id) e.currentTarget.style.background = 'hsl(0,0%,6%)'; }}
              onMouseLeave={e => { if (selected !== c.id) e.currentTarget.style.background = 'transparent'; }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
                    color: 'hsl(0,0%,90%)', fontWeight: 600, marginBottom: 2 }}>{c.name}</div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
                    color: 'hsl(0,0%,45%)', marginBottom: 6 }}>{c.code} · {c.city}, {c.state}</div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <StatusChip status={c.status} />
                    <CategoryChip cat={c.category} />
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
                    color: 'hsl(197,100%,48%)', fontWeight: 700 }}>{fmt(c.total_revenue)}</div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
                    color: isOverLimit(c) ? 'hsl(0,84%,60%)' : 'hsl(0,0%,40%)' }}>
                    {fmt(c.outstanding)} outstanding
                  </div>
                  <WorkflowActions recordType="customer" record={c} userRole={user?.role} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right detail */}
      <div className="split-right" style={{ flex: 1, background: 'hsl(0,0%,4%)', border: '1px solid hsl(0,0%,14%)', overflowY: 'auto' }}>
        {!detail ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            height: '100%', color: 'hsl(0,0%,30%)', gap: 12 }}>
            <Buildings size={40} />
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, textTransform: 'uppercase',
              letterSpacing: '0.15em' }}>Select a customer to view details</div>
          </div>
        ) : (
          <div data-testid="customer-detail" style={{ padding: 24 }}>
            {/* Customer header */}
            <div style={{ marginBottom: 24, paddingBottom: 20, borderBottom: '1px solid hsl(0,0%,12%)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h2 style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 20, fontWeight: 700,
                    color: 'hsl(0,0%,95%)', textTransform: 'uppercase', marginBottom: 4 }}>{detail.name}</h2>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'hsl(0,0%,50%)' }}>
                    {detail.code} · GSTIN: {detail.gstin || 'N/A'} · {detail.city}, {detail.state}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <StatusChip status={detail.status} />
                  <CategoryChip cat={detail.category} />
                </div>
              </div>
            </div>

            {/* KPI row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, marginBottom: 20 }}>
              {[
                { label: 'Total Revenue', value: fmt(detail.total_revenue), accent: 'hsl(197,100%,48%)' },
                { label: 'Outstanding', value: fmt(detail.outstanding),
                  accent: isOverLimit(detail) ? 'hsl(0,84%,60%)' : 'hsl(0,0%,80%)' },
                { label: 'Credit Limit', value: fmt(detail.credit_limit), accent: 'hsl(0,0%,70%)' },
                { label: 'Total Orders', value: detail.total_orders, accent: 'hsl(200,80%,55%)' },
              ].map(m => (
                <div key={m.label} style={{ background: 'hsl(0,0%,6%)', border: '1px solid hsl(0,0%,12%)', padding: 14 }}>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,45%)',
                    textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 6 }}>{m.label}</div>
                  <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 20, fontWeight: 700,
                    color: m.accent }}>{m.value}</div>
                </div>
              ))}
            </div>

            {/* Contact info */}
            <div style={{ background: 'hsl(0,0%,6%)', border: '1px solid hsl(0,0%,12%)', padding: 16, marginBottom: 20 }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'hsl(0,0%,45%)',
                textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 12 }}>Contact</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                {[
                  { label: 'Name', val: detail.contact_name },
                  { label: 'Email', val: detail.contact_email },
                  { label: 'Phone', val: detail.contact_phone },
                  { label: 'WhatsApp', val: detail.whatsapp_phone || detail.contact_phone || '—' },
                  { label: 'Payment Terms', val: detail.payment_terms },
                  { label: 'Address', val: detail.address },
                ].map(f => (
                  <div key={f.label}>
                    <div style={{ fontSize: 9, color: 'hsl(0,0%,40%)', fontFamily: "'JetBrains Mono', monospace",
                      textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 2 }}>{f.label}</div>
                    <div style={{ fontSize: 11, color: 'hsl(0,0%,75%)', fontFamily: "'JetBrains Mono', monospace" }}>
                      {f.val || '—'}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Locations section */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div style={{ ...mono, fontSize: 10, color: 'hsl(0,0%,45%)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
                  Locations
                </div>
                {!showLocForm && (
                  <button onClick={openAddLoc} data-testid="add-location-btn"
                    style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: '1px solid hsl(0,0%,20%)', color: 'hsl(0,0%,60%)', cursor: 'pointer', padding: '4px 10px', ...mono, fontSize: 10 }}>
                    <Plus size={10} weight="bold" /> ADD LOCATION
                  </button>
                )}
              </div>

              {/* Location cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8, marginBottom: showLocForm ? 12 : 0 }}>
                {(detail.locations || []).map(loc => {
                  const tb = typeBadge(loc.type);
                  const isEditing = showLocForm && locEditId === loc.loc_id;
                  return (
                    <div key={loc.loc_id} data-testid={`location-card-${loc.loc_id}`}
                      style={{ background: 'hsl(0,0%,6%)', border: `1px solid ${isEditing ? 'hsl(197,100%,48%)' : 'hsl(0,0%,12%)'}`, padding: 12, position: 'relative' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <MapPin size={11} color="hsl(0,0%,45%)" />
                          <span style={{ ...mono, fontSize: 11, color: 'hsl(0,0%,88%)', fontWeight: 600 }}>{loc.nickname}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ ...mono, fontSize: 8, padding: '1px 5px', ...tb }}>{(loc.type || 'both').toUpperCase()}</span>
                          <button onClick={() => openEditLoc(loc)} title="Edit"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'hsl(0,0%,40%)', padding: 2, display: 'flex' }}>
                            <PencilSimple size={11} />
                          </button>
                          <button onClick={() => deleteLoc(loc.loc_id)} title="Delete"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'hsl(0,84%,55%)', padding: 2, display: 'flex' }}>
                            <Trash size={11} />
                          </button>
                        </div>
                      </div>
                      {loc.city && <div style={{ ...mono, fontSize: 10, color: 'hsl(0,0%,50%)' }}>{loc.city}{loc.state ? `, ${loc.state}` : ''}</div>}
                      {loc.gstin && <div style={{ ...mono, fontSize: 9, color: 'hsl(0,0%,40%)', marginTop: 3 }}>GSTIN: {loc.gstin}</div>}
                      {loc.contact_name && <div style={{ ...mono, fontSize: 9, color: 'hsl(0,0%,40%)', marginTop: 2 }}>{loc.contact_name}{loc.contact_phone ? ` · ${loc.contact_phone}` : ''}</div>}
                    </div>
                  );
                })}
              </div>

              {/* Inline add/edit form */}
              {showLocForm && (
                <div style={{ background: 'hsl(0,0%,5%)', border: '1px solid hsl(197,100%,25%)', padding: 16 }}>
                  <div style={{ ...mono, fontSize: 10, color: 'hsl(197,100%,48%)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>
                    {locEditId ? 'Edit Location' : 'Add Location'}
                  </div>
                  <LocFormFields form={locForm} onChange={(k, v) => setLocForm(p => ({ ...p, [k]: v }))} />
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
                    <button onClick={cancelLocForm} className="btn-ghost" style={{ fontSize: 11 }}>Cancel</button>
                    <button onClick={saveLoc} disabled={locSaving} className="btn-primary" data-testid="save-location-btn" style={{ fontSize: 11 }}>
                      {locSaving ? 'SAVING…' : 'SAVE'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Orders history */}
            <div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'hsl(0,0%,45%)',
                textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 10 }}>Order History</div>
              {detail.orders?.length === 0 ? (
                <div style={{ color: 'hsl(0,0%,35%)', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, padding: 16 }}>
                  No orders yet
                </div>
              ) : (
                <div className="table-scroll">
                <table className="erp-table">
                  <thead>
                    <tr><th>Order No</th><th>Product</th><th>Qty</th><th>Value</th><th>Date</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    {detail.orders?.map(o => (
                      <tr key={o.id}>
                        <td style={{ color: 'hsl(197,100%,48%)' }}>{o.order_no}</td>
                        <td style={{ color: 'hsl(0,0%,70%)' }}>{o.product_sku}</td>
                        <td>{o.quantity?.toLocaleString()}</td>
                        <td>₹{(o.total_value/1000).toFixed(0)}K</td>
                        <td style={{ color: 'hsl(0,0%,55%)' }}>
                          {new Date(o.created_at).toLocaleDateString('en-IN')}
                        </td>
                        <td>
                          <span className={o.status === 'dispatched' ? 'chip-active' :
                            o.status === 'in_production' ? 'chip-info' :
                            o.status === 'confirmed' ? 'chip-warning' : 'chip-neutral'}>
                            {o.status?.replace(/_/g, ' ').toUpperCase()}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Add Customer Modal */}
      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 100,
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,18%)', width: 520, padding: 28 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h3 style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 16, textTransform: 'uppercase',
                color: 'hsl(0,0%,95%)' }}>Add Customer</h3>
              <button onClick={() => setShowAdd(false)} data-testid="close-modal-btn"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'hsl(0,0%,50%)' }}>
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleAdd}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {[
                  { key: 'name', label: 'Company Name', full: true, req: true },
                  { key: 'code', label: 'Customer Code', req: true },
                  { key: 'gstin', label: 'GSTIN' },
                  { key: 'city', label: 'City' },
                  { key: 'state', label: 'State' },
                  { key: 'contact_name', label: 'Contact Name' },
                  { key: 'contact_email', label: 'Contact Email' },
                  { key: 'contact_phone', label: 'Contact Phone' },
                  { key: 'whatsapp_phone', label: 'WhatsApp Phone (for WA notifications)' },
                  { key: 'payment_terms', label: 'Payment Terms' },
                  { key: 'credit_limit', label: 'Credit Limit (₹)' },
                ].map(f => (
                  <div key={f.key} style={{ gridColumn: f.full ? 'span 2' : 'span 1' }}>
                    <label style={{ display: 'block', fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
                      color: 'hsl(0,0%,45%)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 4 }}>
                      {f.label}
                    </label>
                    <input value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                      required={f.req} data-testid={`field-${f.key}`}
                      style={{ width: '100%', background: 'hsl(0,0%,8%)', border: '1px solid hsl(0,0%,16%)',
                        padding: '8px 10px', color: 'hsl(0,0%,85%)', fontFamily: "'JetBrains Mono', monospace",
                        fontSize: 11, outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                ))}
                <div style={{ gridColumn: 'span 1' }}>
                  <label style={{ display: 'block', fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
                    color: 'hsl(0,0%,45%)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 4 }}>
                    Preferred Currency
                  </label>
                  <select value={form.currency} onChange={e => setForm(p => ({ ...p, currency: e.target.value }))}
                    data-testid="field-currency"
                    style={{ width: '100%', background: 'hsl(0,0%,8%)', border: '1px solid hsl(0,0%,16%)',
                      padding: '8px 10px', color: 'hsl(0,0%,85%)', fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 11, outline: 'none', boxSizing: 'border-box' }}>
                    <option value="INR">₹ INR — Indian Rupee</option>
                    <option value="USD">$ USD — US Dollar</option>
                    <option value="EUR">€ EUR — Euro</option>
                    <option value="GBP">£ GBP — British Pound</option>
                    <option value="AED">د.إ AED — UAE Dirham</option>
                  </select>
                </div>
              </div>
              <div style={{ marginTop: 20, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowAdd(false)} className="btn-ghost">Cancel</button>
                <button type="submit" data-testid="save-customer-btn" className="btn-primary">SAVE</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
